class ConstraintInner {
    _wasmInstance;
    _ptr;
    _bodyReference;
    _referenceCount;
    constructor(wasmInstance, ptr, bodyReference) {
        this._wasmInstance = wasmInstance;
        this._ptr = ptr;
        this._bodyReference = bodyReference;
        if (Array.isArray(bodyReference)) {
            bodyReference[0].addReference();
            bodyReference[1].addReference();
        }
        else {
            bodyReference.addReference();
        }
        this._referenceCount = 0;
    }
    dispose() {
        if (this._referenceCount > 0) {
            throw new Error("Cannot dispose constraint while it still has references");
        }
        if (this._ptr === 0) {
            return;
        }
        // this operation is thread-safe because the constraint is not belong to any physics world
        this._wasmInstance.deref()?.destroyConstraint(this._ptr);
        this._ptr = 0;
        if (Array.isArray(this._bodyReference)) {
            this._bodyReference[0].removeReference();
            this._bodyReference[1].removeReference();
        }
        else {
            this._bodyReference.removeReference();
        }
        this._bodyReference = null;
    }
    get ptr() {
        return this._ptr;
    }
    addReference() {
        this._referenceCount += 1;
    }
    removeReference() {
        this._referenceCount -= 1;
    }
    get hasReferences() {
        return 0 < this._referenceCount;
    }
}
function ConstraintFinalizer(inner) {
    inner.dispose();
}
const ConstraintRegistryMap = new WeakMap();
/**
 * Base class for all bullet physics constraints
 */
export class Constraint {
    runtime;
    _inner;
    _worldReference;
    constructor(runtime, ptr, bodyReference) {
        if (Array.isArray(bodyReference)) {
            if (bodyReference[0].runtime !== runtime || bodyReference[1].runtime !== runtime) {
                throw new Error("Cannot create constraint between bodies from different runtimes");
            }
        }
        else {
            if (bodyReference.runtime !== runtime) {
                throw new Error("Cannot create constraint between body and bundle from different runtimes");
            }
        }
        this.runtime = runtime;
        this._inner = new ConstraintInner(new WeakRef(runtime.wasmInstance), ptr, bodyReference);
        this._worldReference = null;
        let registry = ConstraintRegistryMap.get(runtime.wasmInstance);
        if (registry === undefined) {
            registry = new FinalizationRegistry(ConstraintFinalizer);
            ConstraintRegistryMap.set(runtime.wasmInstance, registry);
        }
        registry.register(this, this._inner, this);
    }
    /**
     * Disposes the constraint and releases the resources associated with it
     */
    dispose() {
        if (this._inner.ptr === 0) {
            return;
        }
        this._inner.dispose();
        const registry = ConstraintRegistryMap.get(this.runtime.wasmInstance);
        registry?.unregister(this);
    }
    /**
     * @internal
     */
    get ptr() {
        return this._inner.ptr;
    }
    /**
     * @internal
     */
    addReference() {
        this._inner.addReference();
    }
    /**
     * @internal
     */
    removeReference() {
        this._inner.removeReference();
    }
    /**
     * @internal
     */
    setWorldReference(worldReference) {
        if (this._worldReference !== null && worldReference !== null) {
            throw new Error("Cannot add constraint to multiple worlds");
        }
        if (this._worldReference === worldReference) {
            return;
        }
        this._worldReference = worldReference;
        if (worldReference !== null) {
            this._inner.addReference();
        }
        else {
            this._inner.removeReference();
        }
    }
}
const MatrixBufferSize = 16 * 4 /* Constants.A32BytesPerElement */;
class Generic6DofConstraintBase extends Constraint {
    /**
     * Sets the linear lower limit of the constraint
     *
     * If the constraint is added to a physics world, this operation will wait for the world evaluation to finish
     * @param limit linear lower limit
     */
    setLinearLowerLimit(limit) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintSetLinearLowerLimit(this._inner.ptr, limit.x, limit.y, limit.z);
    }
    /**
     * Sets the linear upper limit of the constraint
     * @param limit linear upper limit
     */
    setLinearUpperLimit(limit) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintSetLinearUpperLimit(this._inner.ptr, limit.x, limit.y, limit.z);
    }
    /**
     * Sets the angular lower limit of the constraint
     * @param limit angular lower limit
     */
    setAngularLowerLimit(limit) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintSetAngularLowerLimit(this._inner.ptr, limit.x, limit.y, limit.z);
    }
    /**
     * Sets the angular upper limit of the constraint
     * @param limit angular upper limit
     */
    setAngularUpperLimit(limit) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintSetAngularUpperLimit(this._inner.ptr, limit.x, limit.y, limit.z);
    }
    /**
     * Sets the parameter of the constraint
     *
     * axis is
     * - 0 for linear x
     * - 1 for linear y
     * - 2 for linear z
     * - 3 for angular x
     * - 4 for angular y
     * - 5 for angular z
     *
     * out of range axis will be ignored
     *
     * @param num parameter number
     * @param value parameter value
     * @param axis parameter axis
     */
    setParam(num, value, axis) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintSetParam(this._inner.ptr, num, value, axis);
    }
    /**
     * Sets whether the constraint uses the frame offset
     *
     * This is used for matching the behavior of MMD
     * @param frameOffsetOnOff true to use the frame offset, false to not use it
     */
    useFrameOffset(frameOffsetOnOff) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintUseFrameOffset(this._inner.ptr, frameOffsetOnOff);
    }
}
/**
 * Generic6DofConstraint is a constraint that allows for 6 degrees of freedom (3 linear and 3 angular) between two rigid bodies
 *
 * It can be used to create a variety of constraints, such as a hinge, slider, or ball-and-socket joint
 */
export class Generic6DofConstraint extends Generic6DofConstraintBase {
    constructor(runtime, bodyAOrBundle, bodyBOrIndices, frameA, frameB, useLinearReferenceFrameA) {
        const wasmInstance = runtime.wasmInstance;
        const frameABufferPtr = wasmInstance.allocateBuffer(MatrixBufferSize);
        const frameABuffer = wasmInstance.createTypedArray(Float32Array, frameABufferPtr, MatrixBufferSize / 4 /* Constants.A32BytesPerElement */);
        frameA.copyToArray(frameABuffer.array);
        const frameBBufferPtr = wasmInstance.allocateBuffer(MatrixBufferSize);
        const frameBBuffer = wasmInstance.createTypedArray(Float32Array, frameBBufferPtr, MatrixBufferSize / 4 /* Constants.A32BytesPerElement */);
        frameB.copyToArray(frameBBuffer.array);
        const isBundleParam = Array.isArray(bodyBOrIndices);
        const ptr = isBundleParam
            ? wasmInstance.createGeneric6DofConstraintFromBundle(bodyAOrBundle.ptr, bodyBOrIndices[0], bodyBOrIndices[1], frameABufferPtr, frameBBufferPtr, useLinearReferenceFrameA)
            : wasmInstance.createGeneric6DofConstraint(bodyAOrBundle.ptr, bodyBOrIndices.ptr, frameABufferPtr, frameBBufferPtr, useLinearReferenceFrameA);
        wasmInstance.deallocateBuffer(frameABufferPtr, MatrixBufferSize);
        wasmInstance.deallocateBuffer(frameBBufferPtr, MatrixBufferSize);
        const bodyReference = isBundleParam
            ? bodyAOrBundle
            : [bodyAOrBundle, bodyBOrIndices];
        super(runtime, ptr, bodyReference);
    }
}
/**
 * Generic6DofSpringConstraint is a constraint that allows for 6 degrees of freedom (3 linear and 3 angular) between two rigid bodies
 *
 * It can be used to create a variety of constraints, such as a hinge, slider, or ball-and-socket joint
 *
 * This constraint also supports springs, which can be used to create a spring-like effect between the two bodies
 */
export class Generic6DofSpringConstraint extends Generic6DofConstraintBase {
    constructor(runtime, bodyAOrBundle, bodyBOrIndices, frameA, frameB, useLinearReferenceFrameA) {
        const wasmInstance = runtime.wasmInstance;
        const frameABufferPtr = wasmInstance.allocateBuffer(MatrixBufferSize);
        const frameABuffer = wasmInstance.createTypedArray(Float32Array, frameABufferPtr, MatrixBufferSize / 4 /* Constants.A32BytesPerElement */);
        frameA.copyToArray(frameABuffer.array);
        const frameBBufferPtr = wasmInstance.allocateBuffer(MatrixBufferSize);
        const frameBBuffer = wasmInstance.createTypedArray(Float32Array, frameBBufferPtr, MatrixBufferSize / 4 /* Constants.A32BytesPerElement */);
        frameB.copyToArray(frameBBuffer.array);
        const isBundleParam = Array.isArray(bodyBOrIndices);
        const ptr = isBundleParam
            ? wasmInstance.createGeneric6DofSpringConstraintFromBundle(bodyAOrBundle.ptr, bodyBOrIndices[0], bodyBOrIndices[1], frameABufferPtr, frameBBufferPtr, useLinearReferenceFrameA)
            : wasmInstance.createGeneric6DofSpringConstraint(bodyAOrBundle.ptr, bodyBOrIndices.ptr, frameABufferPtr, frameBBufferPtr, useLinearReferenceFrameA);
        wasmInstance.deallocateBuffer(frameABufferPtr, MatrixBufferSize);
        wasmInstance.deallocateBuffer(frameBBufferPtr, MatrixBufferSize);
        const bodyReference = isBundleParam
            ? bodyAOrBundle
            : [bodyAOrBundle, bodyBOrIndices];
        super(runtime, ptr, bodyReference);
    }
    /**
     * Enables or disables the spring for the specified index
     * @param index index of the spring
     * @param onOff true to enable the spring, false to disable it
     */
    enableSpring(index, onOff) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintEnableSpring(this._inner.ptr, index, onOff);
    }
    /**
     * Sets the spring stiffness for the specified index
     * @param index index of the spring
     * @param stiffness spring stiffness
     */
    setStiffness(index, stiffness) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintSetStiffness(this._inner.ptr, index, stiffness);
    }
    /**
     * Sets the spring damping for the specified index
     * @param index index of the spring
     * @param damping spring damping
     */
    setDamping(index, damping) {
        if (this._inner.hasReferences) {
            this.runtime.lock.wait();
        }
        this.runtime.wasmInstance.constraintSetDamping(this._inner.ptr, index, damping);
    }
}
